const FEATURE_ITEMS = [
  {
    title: "Build setlists from your own song library",
    text: "Create setlists from the repertoire you already keep in Paranoidi. Keep songs, lyrics, chords and performance material connected instead of maintaining separate lists in different apps.",
  },
  {
    title: "Save sets for different gigs",
    text: "Prepare reusable sets for bars, weddings, private events, corporate gigs and other live shows. Your saved setlists stay available in the browser wherever you perform.",
  },
  {
    title: "Open lyrics and chords on stage",
    text: "Move from your setlist directly into a clear reader view for lyrics, chords and notes during the performance.",
  },
  {
    title: "Use the built-in prompter",
    text: "Let lyrics or notes move automatically while you play. Adjust the display for the stage instead of relying on printed sheets or separate teleprompter tools.",
  },
];

const WORKFLOW_STEPS = [
  {
    number: "01",
    title: "Add your repertoire",
    text: "Build your song library manually or import existing material with supported Paranoidi plans.",
  },
  {
    number: "02",
    title: "Create your setlist",
    text: "Choose songs from your library and arrange them into the order you want for the show.",
  },
  {
    number: "03",
    title: "Use it live",
    text: "Open the setlist on your phone, tablet or computer and move between songs, lyrics, chords and reader tools during the gig.",
  },
];

const USE_CASES = [
  {
    title: "Solo musicians",
    text: "Keep your repertoire, set order, lyrics and chords together for restaurant gigs, pubs, weddings and private events.",
  },
  {
    title: "Bands and duos",
    text: "Prepare the running order before the show and keep a shared structure for what comes next during the performance.",
  },
  {
    title: "Cover bands",
    text: "Organise large repertoires into practical sets instead of searching through scattered song files on stage.",
  },
  {
    title: "Live performers",
    text: "Use the same browser-based workspace for setlists, song requests, gig notes and other performance tools.",
  },
];

const FAQ_ITEMS = [
  {
    question: "Is Paranoidi a setlist app for musicians?",
    answer:
      "Yes. Paranoidi includes setlists and saved sets for live performers, together with a song library, lyrics and chords, reader mode and a prompter.",
  },
  {
    question: "Do I need to install an app?",
    answer:
      "No. Paranoidi is browser-based, so you can use it on compatible phones, tablets and computers without installing a separate audience or performer app.",
  },
  {
    question: "Can I use lyrics and chords with my setlists?",
    answer:
      "Yes. Songs in your Paranoidi library can include lyrics, chords and notes that can be opened in the reader during a gig.",
  },
  {
    question: "Can Paranoidi also handle audience song requests?",
    answer:
      "Yes. Paranoidi combines performer tools with QR code song requests, so your song library can support both your planned set and audience interaction.",
  },
];

export default function SetlistAppLandingPage() {
  return (
    <main className="min-h-screen overflow-hidden bg-[#050505] text-white">
      <style>
        {`
          @keyframes paranoidiSetlistHeroIn {
            0% {
              opacity: 0;
              transform: translateY(28px) scale(0.98);
            }

            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes paranoidiSetlistPulse {
            0%, 100% {
              opacity: 0.3;
              transform: scale(1);
            }

            50% {
              opacity: 0.7;
              transform: scale(1.08);
            }
          }

          details[open] .paranoidi-setlist-faq-icon {
            transform: rotate(45deg);
            border-color: rgba(248, 113, 113, 0.45);
            background: rgba(239, 68, 68, 0.12);
          }

          @media (prefers-reduced-motion: reduce) {
            [class*="paranoidiSetlistHeroIn"],
            [class*="paranoidiSetlistPulse"] {
              animation: none !important;
            }
          }
        `}
      </style>

      <section className="relative min-h-screen overflow-hidden border-b border-white/10">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(239,68,68,0.34),transparent_30rem),radial-gradient(circle_at_80%_15%,rgba(255,255,255,0.10),transparent_26rem),linear-gradient(135deg,rgba(127,29,29,0.28),transparent_48%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:44px_44px]" />
        <div className="pointer-events-none absolute left-1/2 top-10 h-96 w-96 -translate-x-1/2 rounded-full bg-red-500/10 blur-3xl [animation:paranoidiSetlistPulse_5s_ease-in-out_infinite]" />

        <div className="relative mx-auto grid min-h-screen max-w-[96rem] gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[0.84fr_1.16fr] lg:items-center lg:px-10">
          <div className="[animation:paranoidiSetlistHeroIn_700ms_ease-out_both]">
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              Setlists for live performers
            </p>

            <h1 className="mt-5 text-[2.85rem] font-black uppercase leading-[0.84] tracking-[-0.08em] text-white sm:text-6xl lg:text-8xl">
              Setlist app for musicians and live performers
            </h1>

            <p className="mt-7 max-w-3xl text-lg font-semibold leading-8 text-zinc-300 sm:text-xl sm:leading-9">
              Build setlists from your own song library, open lyrics and chords
              on stage and use reader mode and a prompter during the gig.
              Paranoidi keeps your live performance tools together in one
              browser-based workspace.
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-full bg-red-500 px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white shadow-lg shadow-red-950/30 transition hover:-translate-y-0.5 hover:bg-red-400"
              >
                Start your 14-day trial
              </a>

              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.04] px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:bg-white/[0.08]"
              >
                See how it works
              </a>
            </div>
          </div>

          <div className="relative [animation:paranoidiSetlistHeroIn_900ms_ease-out_120ms_both]">
            <div className="absolute -inset-6 rounded-[3rem] bg-red-500/10 blur-3xl" />

            <div className="relative overflow-hidden rounded-[2.25rem] border border-white/10 bg-black shadow-2xl shadow-black/50">
              <img
                src="/assets/paranoidi/paranoidi-setlist-reader-en.png"
                alt="Paranoidi setlist, reader mode and prompter for musicians performing live"
                className="h-full w-full object-cover"
                loading="eager"
              />

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/10" />

              <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/10 bg-black/65 px-5 py-4 backdrop-blur">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-red-300">
                  One live workflow
                </p>

                <p className="mt-1 text-xl font-black text-white">
                  Setlists · lyrics · chords · reader · prompter
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
        <div className="max-w-3xl">
          <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
            Built for the stage
          </p>

          <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
            Your setlist should do more than list song titles.
          </h2>

          <p className="mt-5 text-base font-semibold leading-8 text-zinc-400">
            A live set changes quickly. You need the next song, the right key,
            lyrics, chords and notes without jumping between paper sheets,
            documents and separate apps. Paranoidi connects the setlist to the
            song library you already use.
          </p>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {FEATURE_ITEMS.map((item) => (
            <article
              key={item.title}
              className="rounded-[2rem] border border-white/10 bg-white/[0.035] p-6 transition duration-300 hover:-translate-y-1 hover:border-red-400/30 hover:bg-white/[0.055]"
            >
              <h3 className="text-2xl font-black uppercase tracking-[-0.05em] text-white">
                {item.title}
              </h3>

              <p className="mt-4 text-sm font-semibold leading-7 text-zinc-400">
                {item.text}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section
        id="how-it-works"
        className="scroll-mt-8 border-y border-white/10 bg-white/[0.025]"
      >
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              How it works
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              From song library to stage in three steps.
            </h2>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {WORKFLOW_STEPS.map((step) => (
              <article
                key={step.number}
                className="rounded-[2rem] border border-white/10 bg-black/35 p-6"
              >
                <p className="text-sm font-black uppercase tracking-[0.18em] text-red-400">
                  {step.number}
                </p>

                <h3 className="mt-4 text-2xl font-black uppercase leading-none tracking-[-0.05em] text-white">
                  {step.title}
                </h3>

                <p className="mt-4 text-sm font-semibold leading-7 text-zinc-400">
                  {step.text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#070707]">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-10 lg:py-20">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              More than setlists
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              Keep your whole gig workflow connected.
            </h2>

            <p className="mt-5 text-base font-semibold leading-8 text-zinc-400">
              Your setlist does not live in isolation. Paranoidi connects it
              with your repertoire, lyrics and chords, reader mode, prompter,
              gig notes and audience song requests.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="/en/features"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:border-red-400/40 hover:bg-white/[0.08]"
              >
                View all features
              </a>

              <a
                href="/en/song-requests"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:border-red-400/40 hover:bg-white/[0.08]"
              >
                Song request app
              </a>
            </div>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-black/45 p-6">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-red-300">
              Browser-based
            </p>

            <p className="mt-4 text-3xl font-black uppercase tracking-[-0.05em] text-white">
              One place for the songs you plan and the songs your audience asks
              for.
            </p>

            <p className="mt-5 text-sm font-semibold leading-7 text-zinc-400">
              Prepare your show before the gig and keep the same repertoire
              available when audience requests start coming in.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-black">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              Made for live performers
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              Setlists for real gigs.
            </h2>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {USE_CASES.map((item) => (
              <article
                key={item.title}
                className="rounded-[2rem] border border-white/10 bg-white/[0.035] p-6"
              >
                <h3 className="text-2xl font-black uppercase tracking-[-0.05em] text-white">
                  {item.title}
                </h3>

                <p className="mt-4 text-sm font-semibold leading-7 text-zinc-400">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-white/[0.025]">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:px-10 lg:py-20">
          <div className="max-w-xl">
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              Frequently asked questions
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              Setlists without another disconnected tool.
            </h2>
          </div>

          <div className="grid gap-3">
            {FAQ_ITEMS.map((item) => (
              <details
                key={item.question}
                className="group rounded-[1.5rem] border border-white/10 bg-black/40 px-5 py-4 open:border-red-400/30 open:bg-white/[0.045]"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-base font-black text-white">
                  <span>{item.question}</span>

                  <span
                    aria-hidden="true"
                    className="paranoidi-setlist-faq-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-red-300 transition duration-200"
                  >
                    +
                  </span>
                </summary>

                <p className="mt-4 max-w-3xl border-t border-white/10 pt-4 text-sm font-semibold leading-7 text-zinc-400">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#090303]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.20),transparent_34rem)]" />

        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:px-10 lg:py-20">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-300">
              Paranoidi
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              Build your next setlist in Paranoidi.
            </h2>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-black/45 p-6 shadow-2xl shadow-black/30">
            <p className="text-2xl font-black uppercase tracking-[-0.05em] text-white">
              Song library, setlists and live tools in one browser-based
              workspace.
            </p>

            <div className="mt-6 grid gap-3">
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-full bg-red-500 px-6 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-red-400"
              >
                Start your 14-day trial
              </a>

              <a
                href="/en/features"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.04] px-6 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-white/[0.08]"
              >
                Explore all features
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

